import { Box, Tooltip, Typography } from "@mui/material";
import {
	CoreButton,
	CoreCheckbox,
	CoreDataView,
	CoreDataViewColumn,
	CoreMenuButton,
	CoreMenuItem,
	CorePagination,
	CoreTextField,
	SortConfig,
} from "../core";
import { useEffect, useMemo, useState, useCallback } from "react";
import i18n from "../i18n/i18n";
import { EmptyBoxIcon } from "./icons";
import type { FlaggedKeyword } from "@verity/database/prisma";

export type FlaggedKeywordWithInstituteLevel = FlaggedKeyword & { isInstituteLevel?: boolean };

type WithId = {
	id: number;
};
type DraftKeyword = FlaggedKeywordWithInstituteLevel & WithId;

type WithRowState = {
	disabled: boolean;
};
type WithActionState = {
	selected: boolean;
	editing: boolean;
};
type Row = DraftKeyword & WithRowState & WithActionState;

const PAGE_SIZE = 4;

const convertToComparableString = (keyword: string) => keyword.trim().toLowerCase() as string & { __comparable: true };

const useDraftKeywords = (
	initialKeywords: FlaggedKeywordWithInstituteLevel[],
	onSettingChange: (keywords: FlaggedKeywordWithInstituteLevel[]) => void,
) => {
	const [draftKeywords, setDraftKeywords] = useState<DraftKeyword[]>([]);
	useEffect(() => {
		setDraftKeywords(initialKeywords.map((kw, id) => ({ ...kw, id })));
	}, [initialKeywords]);

	const addKeyword = useCallback(
		(flaggedKeyword: FlaggedKeyword) => {
			const updated = [{ ...flaggedKeyword }, ...draftKeywords] // this naturally puts new keywords first
				.map((kw, id) => ({ ...kw, id }));
			setDraftKeywords(updated);
			onSettingChange(updated);
		},
		[draftKeywords, onSettingChange],
	);

	const updateKeyword = useCallback(
		(id: number, updatedFlaggedKeyword: FlaggedKeyword) => {
			const updated = [...draftKeywords];
			const keywordIndex = updated.findIndex((keyword) => keyword.id === id);
			if (keywordIndex !== -1) {
				updated[keywordIndex] = { ...updated[keywordIndex], ...updatedFlaggedKeyword };
			}
			setDraftKeywords(updated);
			onSettingChange(updated);
		},
		[draftKeywords, onSettingChange],
	);

	const deleteKeyword = useCallback(
		(id: number) => {
			const updated = draftKeywords.filter(({ id: keywordId }) => keywordId !== id);
			setDraftKeywords(updated);
			onSettingChange(updated);
		},
		[draftKeywords, onSettingChange],
	);

	const deleteKeywordMultiple = useCallback(
		(ids: number[]) => {
			const updated = draftKeywords.filter(({ id: keywordId }) => !ids.includes(keywordId));
			setDraftKeywords(updated);
			onSettingChange(updated);
		},
		[draftKeywords, onSettingChange],
	);
	return [draftKeywords, addKeyword, updateKeyword, deleteKeyword, deleteKeywordMultiple] as const;
};

const useFilteredTableRows = (tableRows: Row[]) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedQuery(searchQuery);
		}, 300);
		return () => clearTimeout(timeout);
	}, [searchQuery]);

	const filteredTableRows = useMemo(() => {
		const trimmedLowerDebouncedQuery = convertToComparableString(debouncedQuery);

		if (!trimmedLowerDebouncedQuery) {
			return tableRows;
		} else {
			return tableRows.filter((row) => {
				const trimmedLowerKeyword = convertToComparableString(row.keyword);
				return trimmedLowerKeyword.includes(trimmedLowerDebouncedQuery);
			});
		}
	}, [tableRows, debouncedQuery]);

	return [filteredTableRows, searchQuery, setSearchQuery] as const;
};

const useOrderedTableRows = (tableRows: Row[]) => {
	const [sortConfig, setSortConfig] = useState<SortConfig<Row>[]>([]);

	useEffect(() => {
		setSortConfig([]);
	}, [tableRows]);

	const orderedTableRows = useMemo(() => {
		const sortedRows = [...tableRows];
		for (const { key, direction } of sortConfig) {
			if (key === "keyword") {
				sortedRows.sort((a, b) => {
					const trimmedLowerKeywordA = convertToComparableString(a.keyword);
					const trimmedLowerKeywordB = convertToComparableString(b.keyword);
					return direction === "asc"
						? trimmedLowerKeywordA.localeCompare(trimmedLowerKeywordB)
						: trimmedLowerKeywordB.localeCompare(trimmedLowerKeywordA);
				});
			}
		}
		return sortedRows;
	}, [tableRows, sortConfig]);

	return [orderedTableRows, sortConfig, setSortConfig] as const;
};

const usePaginatedTableRows = (tableRows: Row[]) => {
	const [page, setPage] = useState(1);
	const totalPages = Math.max(1, Math.ceil(tableRows.length / PAGE_SIZE));

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages]);

	const paginatedRows = useMemo(() => {
		const startIdx = (page - 1) * PAGE_SIZE;
		return tableRows.slice(startIdx, startIdx + PAGE_SIZE);
	}, [tableRows, page]);

	return [paginatedRows, page, setPage, totalPages] as const;
};

const useTableRowActions = (tableRows: Row[]) => {
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [editingId, setEditingId] = useState<number | null>(null);

	useEffect(() => {
		setEditingId(null);
		setSelectedIds(new Set());
	}, [tableRows]);

	const toggleSelectOne = useCallback((id: number, selected: boolean) => {
		if (selected) {
			setSelectedIds((prev) => new Set([...prev, id]));
		} else {
			setSelectedIds((prev) => new Set([...prev].filter((i) => i !== id)));
		}
		setEditingId(null);
	}, []);

	const toggleEditOne = useCallback((id: number) => {
		setEditingId((prev) => (prev === id ? null : id));
		setSelectedIds(new Set());
	}, []);

	const clearEditing = useCallback(() => {
		setEditingId(null);
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedIds(new Set());
	}, []);

	return {
		selectedIds,
		editingId,
		setEditingId,
		toggleSelectOne,
		toggleEditOne,
		clearEditing,
		clearSelection,
	};
};

export function FlaggedKeywordsTable(props: {
	viewingContext: "quiz" | "institute";
	keywords: FlaggedKeywordWithInstituteLevel[];
	onSettingChange: (keywords: FlaggedKeywordWithInstituteLevel[]) => void;
	loading?: boolean;
}) {
	const { viewingContext, keywords, onSettingChange, loading } = props;

	// raw keywords
	const [draftKeywords, addDraftKeyword, updateDraftKeyword, _deleteDraftKeyword, deleteDraftKeywordMultiple] =
		useDraftKeywords(keywords, onSettingChange);

	// base table rows (without selection/editing state)
	const baseTableRows = useMemo((): Row[] => {
		return draftKeywords.map((row) => ({
			...row,
			disabled: Boolean(row.isInstituteLevel && viewingContext === "quiz"),
			selected: false,
			editing: false,
		}));
	}, [draftKeywords, viewingContext]);

	// table filtering, sorting, pagination
	const [filteredTableRows, searchQuery, setSearchQuery] = useFilteredTableRows(baseTableRows);
	const [orderedTableRows, sortConfig, setSortConfig] = useOrderedTableRows(filteredTableRows);
	const [paginatedRows, page, setPage, totalPages] = usePaginatedTableRows(orderedTableRows);

	// table actions (selection, editing)
	const tableActions = useTableRowActions(paginatedRows);

	// derive final rows with selection/editing state
	const tableRows = useMemo((): Row[] => {
		return paginatedRows.map((row) => ({
			...row,
			selected: tableActions.selectedIds.has(row.id),
			editing: tableActions.editingId === row.id,
		}));
	}, [paginatedRows, tableActions.selectedIds, tableActions.editingId]);

	// handlers
	const canAddOrEdit = useCallback(
		(keyword: string) => {
			const existingKeywords = baseTableRows.map((x) => convertToComparableString(x.keyword));

			const isKeywordAlreadySet = existingKeywords.includes(convertToComparableString(keyword));
			const isKeywordEmpty = keyword.trim().length === 0;
			const hasSpecialCharacters = !/^[a-zA-Z'-\s]+$/.test(keyword.trim());
			const isKeywordInvalid = isKeywordAlreadySet || isKeywordEmpty || hasSpecialCharacters;

			return !isKeywordInvalid;
		},
		[baseTableRows],
	);

	const handleAdd = useCallback(
		(keyword: string) => {
			const trimmed = keyword.trim();
			if (trimmed) {
				addDraftKeyword({ keyword: trimmed });
				setSearchQuery("");
			}
		},
		[addDraftKeyword, setSearchQuery],
	);

	const handleDeleteSelected = useCallback(() => {
		const idsToDelete = Array.from(tableActions.selectedIds).sort((a, b) => b - a);
		deleteDraftKeywordMultiple(idsToDelete);
	}, [deleteDraftKeywordMultiple, tableActions]);

	const allSelected = useMemo(() => {
		const areSomeNonDisabled = tableRows.some((x) => !x.disabled);
		const areAllNonDisabledSelected = tableRows.every((x) => x.disabled || x.selected);
		return areSomeNonDisabled && areAllNonDisabledSelected;
	}, [tableRows]);

	const hasSelection = useMemo(() => {
		return tableRows.some((x) => x.selected);
	}, [tableRows]);

	const areAllDisabled = useMemo(() => {
		return tableRows.every((x) => x.disabled);
	}, [tableRows]);

	const columns: CoreDataViewColumn<Row>[] = [
		{
			key: "selected",
			header: (
				<CoreCheckbox
					checked={allSelected}
					disabled={areAllDisabled}
					onChange={(e) => {
						paginatedRows.forEach((x) => {
							if (x.disabled) return;
							tableActions.toggleSelectOne(x.id, e.target.checked);
						});
					}}
					inputProps={{ "aria-label": i18n.t("settings.flaggedKeywords.ariaLabel.selectAllVisible") }}
				/>
			),
			cell: (item) => (
				<CoreCheckbox
					disabled={item.disabled}
					checked={item.selected}
					onChange={(e) => tableActions.toggleSelectOne(item.id, e.target.checked)}
					inputProps={{ "aria-label": i18n.t("settings.flaggedKeywords.selectKeyword", { keyword: item.keyword }) }}
				/>
			),
			width: "1%",
		},
		{
			key: "keyword",
			header: <Typography sx={{ fontWeight: "500" }}>{i18n.t("settings.flaggedKeywords.keyword")}</Typography>,
			cell: (item) => (
				<KeywordCell
					row={item}
					onUpdate={updateDraftKeyword}
					onRemove={tableActions.toggleEditOne}
					canAddOrEdit={canAddOrEdit}
				/>
			),
			sortable: true,
		},
		{
			key: "id" as keyof Row,
			header: <Typography sx={{ fontWeight: "500" }}>{i18n.t("settings.flaggedKeywords.actions")}</Typography>,
			cell: (item) => (
				<ActionsCell
					onEdit={() => {
						if (item.disabled) return;
						tableActions.toggleEditOne(item.id);
					}}
					row={item}
				/>
			),
			width: "1%",
		},
	];

	return (
		<Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
			<Typography sx={{ color: "#6B6B6B", fontSize: "15px", fontWeight: "400" }}>
				{i18n.t("settings.flaggedKeywords.subtitle")}
			</Typography>
			<Box sx={{ height: "25px" }} />

			<KeywordsToolbar
				searchQuery={searchQuery}
				onSearchChange={setSearchQuery}
				handleAdd={handleAdd}
				canAddOrEdit={canAddOrEdit}
			/>

			{hasSelection && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
					<CoreButton
						onClick={handleDeleteSelected}
						color="cancel"
						size="large"
						sx={{ width: "110px", fontSize: "15px", height: "35px" }}
					>
						{i18n.t("settings.flaggedKeywords.table.actions.delete")}
					</CoreButton>
				</Box>
			)}

			<Box sx={{ height: "15px" }} />

			{tableRows.length === 0 ? (
				<EmptyState />
			) : (
				<CoreDataView<Row>
					loading={loading}
					sortConfig={sortConfig}
					onSortChange={(sortConfig) => {
						tableActions.clearEditing();
						tableActions.clearSelection();
						setSortConfig(sortConfig);
					}}
					disableInternalSorting
					id="flagged-keywords-table"
					data={tableRows}
					columns={columns}
					skeletonRowCount={PAGE_SIZE}
					noResultsMessage={i18n.t("settings.flaggedKeywords.table.noResults.title")}
					noResultsDescription={i18n.t("settings.flaggedKeywords.table.noResults.subtitle")}
					slots={{
						tableWrapper: { style: { margin: 0 } },
						table: { style: { minWidth: "100%", maxWidth: "100%" } },
					}}
					renderers={{
						rowTooltip: (item) => {
							if (!item.disabled) {
								return null;
							}

							return i18n.t("settings.flaggedKeywords.table.tooltip.disabled");
						},
					}}
				/>
			)}

			<Box sx={{ height: "25px" }} />

			{orderedTableRows.length > PAGE_SIZE && (
				<Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
					<CorePagination page={page} count={totalPages} onChange={(_, p) => setPage(p)} />
				</Box>
			)}
		</Box>
	);
}

function KeywordsToolbar({
	searchQuery,
	onSearchChange,
	handleAdd,
	canAddOrEdit,
}: {
	searchQuery: string;
	onSearchChange: (value: string) => void;
	handleAdd: (value: string) => void;
	canAddOrEdit: (value: string) => boolean;
}) {
	return (
		<Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1.25 }}>
			<Typography sx={{ fontSize: "15px", fontWeight: "600", color: "#000000", whiteSpace: "nowrap" }}>
				{i18n.t("settings.flaggedKeywords.toolBar.title")}
			</Typography>
			<CoreTextField
				ariaLabel={i18n.t("settings.flaggedKeywords.toolBar.title")}
				placeholder={i18n.t("settings.flaggedKeywords.toolBar.placeholder")}
				value={searchQuery}
				onChange={(e) => onSearchChange(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter" && canAddOrEdit(searchQuery)) handleAdd(searchQuery);
				}}
				sx={{ flex: 1 }}
			/>
			<CoreButton
				onClick={() => handleAdd(searchQuery)}
				size="large"
				sx={{ width: "110px", fontSize: "15px" }}
				disabled={!canAddOrEdit(searchQuery)}
			>
				{i18n.t("settings.flaggedKeywords.toolBar.button")}
			</CoreButton>
		</Box>
	);
}

function KeywordCell({
	row,
	onUpdate,
	onRemove,
	canAddOrEdit,
}: {
	row: Row;
	onUpdate: (id: number, updatedFlaggedKeyword: FlaggedKeyword) => void;
	onRemove: (id: number) => void;
	canAddOrEdit: (value: string) => boolean;
}) {
	const [value, setValue] = useState(row.keyword);

	useEffect(() => {
		if (row.editing) {
			setValue(row.keyword);
		}
	}, [row.editing, row.keyword]);

	if (!row.editing) {
		return (
			<Box sx={{ height: "28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Box
					sx={{
						flex: 1,
						minWidth: 0,
					}}
				>
					{row.disabled ? (
						<Typography
							sx={{
								fontSize: "15px",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%",
								width: "min-content",
							}}
						>
							{row.keyword}
						</Typography>
					) : (
						<Tooltip title={row.keyword}>
							<Typography
								sx={{
									fontSize: "15px",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									maxWidth: "100%",
									width: "min-content",
								}}
							>
								{row.keyword}
							</Typography>
						</Tooltip>
					)}
				</Box>
				{row.disabled && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							backgroundColor: "#F5E5F4",
							color: "#344054",
							padding: "2px 10px",
							borderRadius: "4px",
							fontSize: "15px",
							fontWeight: 500,
							whiteSpace: "nowrap",
							flexShrink: 0,
							height: "28px",
						}}
					>
						<Typography>{i18n.t("settings.flaggedKeywords.table.row.badge")}</Typography>
					</Box>
				)}
			</Box>
		);
	}

	const handleSave = () => {
		const trimmed = value.trim();
		if (trimmed) {
			onUpdate(row.id, { keyword: trimmed });
		}
	};

	return (
		<Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "28px" }}>
			<CoreTextField
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") handleSave();
				}}
				autoFocus
				size="small"
				sx={{ flex: 1, minHeight: 0 }}
				InputProps={{ sx: { fontSize: "15px", height: "35px" } }}
			/>
			<CoreButton
				onClick={handleSave}
				color="primary"
				size="small"
				sx={{ width: "87px", height: "35px" }}
				disabled={!canAddOrEdit(value)}
			>
				{i18n.t("settings.flaggedKeywords.table.row.actions.save")}
			</CoreButton>
			<CoreButton onClick={() => onRemove(row.id)} color="cancel" size="small" sx={{ width: "87px", height: "35px" }}>
				{i18n.t("settings.flaggedKeywords.table.row.actions.discard")}
			</CoreButton>
		</Box>
	);
}

function ActionsCell({ onEdit, row }: { onEdit: () => void; row: Row }) {
	const [open, setOpen] = useState(false);

	return (
		<Box sx={{ display: "flex", justifyContent: "center" }}>
			<CoreMenuButton menuShowing={open} setMenuShowing={setOpen} disabled={row.disabled}>
				<CoreMenuItem
					onClick={() => {
						setOpen(false);
						onEdit();
					}}
				>
					{i18n.t("settings.flaggedKeywords.table.row.actions.edit")}
				</CoreMenuItem>
			</CoreMenuButton>
		</Box>
	);
}

function EmptyState() {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				mt: 5,
			}}
		>
			<EmptyBoxIcon sx={{ width: "175px", height: "97px" }} />
			<Box sx={{ height: "30px" }} />
			<Typography sx={{ fontSize: "20px", fontWeight: "600" }}>
				{i18n.t("settings.flaggedKeywords.table.noResults.title")}
			</Typography>
			<Box sx={{ height: "20px" }} />
			<Typography sx={{ fontSize: "15px", fontWeight: "400" }}>
				{i18n.t("settings.flaggedKeywords.table.noResults.subtitle")}
			</Typography>
		</Box>
	);
}
